import { z } from 'zod';
import { RACQUET_SPORT_IDS } from '../constants/racquetSports';

export const ACADEMY_SIZE_BANDS = ['1-25', '26-100', '101-300', '300+'] as const;
export type AcademySizeBand = (typeof ACADEMY_SIZE_BANDS)[number];

export const OPERATING_ROLES = ['solo_coach', 'owner_coach', 'owner_manager'] as const;
export type OperatingRole = (typeof OPERATING_ROLES)[number];

export const COACH_COUNT_BANDS = ['0', '1', '2-5', '6+'] as const;
export type CoachCountBand = (typeof COACH_COUNT_BANDS)[number];

export const LOCATION_COUNT_BANDS = ['1', '2-3', '4+'] as const;
export type LocationCountBand = (typeof LOCATION_COUNT_BANDS)[number];

export const ONBOARDING_GOALS = [
  'reduce_admin',
  'grow_enrollment',
  'simplify_scheduling',
  'improve_communication',
  'manage_payments',
  'player_development',
] as const;
export type OnboardingGoal = (typeof ONBOARDING_GOALS)[number];

export const ONBOARDING_WORKFLOWS = [
  'group_programs',
  'private_lessons',
  'camps',
  'online_payments',
  'waitlists',
  'makeup_credits',
  'programs_pathways',
  'coach_attendance',
  'multi_location',
  'coach_portals',
  'student_portals',
  'student_self_booking',
] as const;
export type OnboardingWorkflow = (typeof ONBOARDING_WORKFLOWS)[number];

const academySportSelectionSchema = z.object({
  sportId: z.enum(RACQUET_SPORT_IDS),
  customLabel: z.string().max(80).optional().nullable(),
  isPrimary: z.boolean().optional(),
});

const academyOperatingProfileBaseSchema = z.object({
  sports: z.array(academySportSelectionSchema).min(1).max(6),
  sizeBand: z.enum(ACADEMY_SIZE_BANDS).optional().nullable(),
  operatingRole: z.enum(OPERATING_ROLES).optional().nullable(),
  coachCountBand: z.enum(COACH_COUNT_BANDS).optional().nullable(),
  locationCountBand: z.enum(LOCATION_COUNT_BANDS).optional().nullable(),
  goals: z.array(z.enum(ONBOARDING_GOALS)).optional(),
  desiredWorkflows: z.array(z.enum(ONBOARDING_WORKFLOWS)).optional(),
  coachesNeedPortalAccess: z.boolean().optional(),
  studentsNeedAccounts: z.boolean().optional(),
  studentsInitiateBookings: z.boolean().optional(),
  needsOnlinePayments: z.boolean().optional(),
  onboardingProfileCompletedAt: z.string().datetime().optional().nullable(),
  onboardingDefaultsAppliedAt: z.string().datetime().optional().nullable(),
  starterLimitationsAcknowledgedAt: z.string().datetime().optional().nullable(),
});

export const academyOperatingProfileSchema = academyOperatingProfileBaseSchema.superRefine((data, ctx) => {
    const primaryCount = data.sports.filter((s) => s.isPrimary).length;
    if (primaryCount !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Exactly one sport must be marked as primary',
        path: ['sports'],
      });
    }
    const other = data.sports.find((s) => s.sportId === 'other');
    if (other && !other.customLabel?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Custom label is required when sport is Other',
        path: ['sports'],
      });
    }
  });

export type AcademyOperatingProfileInput = z.infer<typeof academyOperatingProfileSchema>;

export const academyOnboardingProgressSchema = z.object({
  version: z.number().int().min(1).default(1),
  currentStepId: z.string().min(1),
  completedStepIds: z.array(z.string()).default([]),
  answers: academyOperatingProfileBaseSchema.partial().optional(),
  skippedAt: z.string().datetime().optional().nullable(),
  completedAt: z.string().datetime().optional().nullable(),
});

export type AcademyOnboardingProgressInput = z.infer<typeof academyOnboardingProgressSchema>;
